import ProductState from 'classes/productstate';
import User from 'classes/user';
import EventBus from 'components/event-bus';
import auth from 'controllers/auth';
import navigate from 'controllers/navigate';
import * as DialogService from 'services/dialog';
import {
	ERRORS_NO_RESPONSE,
	ERRORS_OFFLINE,
} from 'settings/errors';
import {
	CartItemsModule,
	ProductsModule,
	ProductStateModule,
	ShippingModule,
	UserModule,
} from 'store';
import {
	NavigationGuard,
	NavigationGuardNext,
	RouteConfig,
} from 'vue-router';

const ConfirmModule = () => import(/* webpackChunkName: "end" */ './modules/confirm.vue');
const CartModule = () => import(/* webpackChunkName: "webshop" */ './modules/cart.vue');
const CreateModule = () => import(/* webpackChunkName: "start" */ './modules/create');
const CropItemModule = () => import(/* webpackChunkName: "crop" */ './modules/crop-item/template.vue');
const CropOverviewModule = () => import(/* webpackChunkName: "crop" */ './modules/crop-overview/template.vue');
const EditorSwitchModule = () => import(/* webpackChunkName: "edit" */ './modules/editor-switch');
const EndModule = () => import(/* webpackChunkName: "end" */ './modules/end.vue');
const ImportModule = () => import(/* webpackChunkName: "select" */ './modules/import.vue');
const LauncherModule = () => import(/* webpackChunkName: "start" */ './modules/launcher.vue');
const LibraryModule = () => import(/* webpackChunkName: "select" */ './modules/library');
const OauthModule = () => import(/* webpackChunkName: "start" */ './modules/oauth.vue');
const OrderStatusModule = () => import(/* webpackChunkName: "account" */ './modules/orderstatus.vue');
const OverviewModule = () => import(/* webpackChunkName: "edit" */ './modules/overview');
const PreviewModule = () => import(/* webpackChunkName: "edit" */ './modules/preview.vue');
const SelectModule = () => import(/* webpackChunkName: "select" */ './modules/select.vue');
const ShelfModule = () => import(/* webpackChunkName: "start" */ './modules/shelf.vue');
const ShowcaseModule = () => import(/* webpackChunkName: "showcase" */ './modules/showcase.vue');
const SortModule = () => import(/* webpackChunkName: "edit" */ './modules/sort/template.vue');
const UserAccountModule = () => import(/* webpackChunkName: "account" */ './modules/account.vue');
const UserAccountPhotosModule = () => import(/* webpackChunkName: "account" */ './modules/account-photos.vue');
const WebshopModule = () => import(/* webpackChunkName: "webshop" */ './modules/webshop.vue');
const WebshopConfirmModule = () => import(/* webpackChunkName: "webshop" */ './modules/webshop-confirm.vue');
const WebshopPaymentModule = () => import(/* webpackChunkName: "webshop" */ './modules/webshop-payment.vue');
const WebshopResultModule = () => import(/* webpackChunkName: "webshop" */ './modules/webshop-result.vue');
const ExternalUploadModule = () => import(/* webpackChunkName: "externalupload" */ './modules/external-upload');

function productAccessErrorHandler(
	err: Error,
	next: NavigationGuardNext<Vue>,
) {
	let errorMessage = err.message;

	if (
		err.message === ERRORS_NO_RESPONSE
		|| err.message === ERRORS_OFFLINE
	) {
		errorMessage = window.App.router.$t('dialogTextLoadError');
	}

	const closeError = DialogService.openErrorDialog({
		header: {
			hasCloseButton: false,
		},
		body: {
			content: errorMessage,
		},
		footer: {
			buttons: [
				{
					id: 'cancel',
					text: window.App.router.$t('dialogButtonCancel'),
					click: () => {
						next(false);
						navigate.toStart();
						closeError();
					},
				},
				{
					id: 'accept',
					text: window.App.router.$t('dialogButtonOfflineOk'),
					click: () => {
						next(false);
						window.location.reload();
						closeError();
					},
				},
			],
		},
	});
}

const productAccess: NavigationGuard<Vue> = (to, from, next) => {
	// This route is only available to logged in users
	if (!UserModule.id) {
		EventBus.$once(
			'auth:login',
			(success: boolean) => {
				if (success) {
					ProductState
						.setup(parseInt(
							to.params.productid,
							10,
						))
						.then(() => {
							next();
						})
						.catch((err) => {
							productAccessErrorHandler(
								err,
								next,
							);
						});
				} else {
					next(false);
					navigate.toStart();
				}
			},
		);

		auth.showLogin({
			hasclose: false,
		});
	} else {
		ProductState
			.setup(parseInt(
				to.params.productid,
				10,
			))
			.then(() => {
				next();
			})
			.catch((err) => {
				productAccessErrorHandler(
					err,
					next,
				);
			});
	}
};

const productSetup: NavigationGuard = (to, from, next) => {
	if (UserModule.id) {
		ProductState
			.setup(parseInt(
				to.params.productid,
				10,
			))
			.finally(() => {
				next();
			});
	} else {
		next();
	}
};

const saveProduct: NavigationGuard = (to, from, next) => {
	ProductState.finalize(
		true,
		true,
	).then(
		(saved) => {
			if (saved) {
				next();
			} else {
				next(false);
				window.App.router.reload();
			}
		},
		(err) => {
			next(false);

			if (!UserModule.id) {
				// User not logged in, discard this project
				ProductStateModule.reset();
				navigate.toStart();
			} else {
				const closeError = DialogService.openErrorDialog({
					header: {
						hasCloseButton: false,
					},
					body: {
						content: (
							err && err.message
								? err.message
								: window.App.router.$t('dialogTextError')
						),
					},
					footer: {
						buttons: [
							{
								id: 'delete',
								text: window.App.router.$t('buttonDiscardChanges'),
								click: () => {
									ProductStateModule.reset();
									navigate.toStart();
									closeError();
								},
							},
							{
								id: 'retry',
								text: window.App.router.$t('dialogButtonErrorRetry'),
								click: () => {
									window.App.router.reload();
									closeError();
								},
							},
						],
					},
				});
			}
		},
	);
};

const userAccess: NavigationGuard = (to, from, next) => {
	// This route is only available to logged in users
	if (!UserModule.id) {
		EventBus.$once(
			'auth:login',
			(success: boolean) => {
				if (success) {
					next();
				} else {
					next(false);
					navigate.toStart();
				}
			},
		);

		auth.showLogin({
			hasclose: false,
		});
	} else {
		ProductState
			.finalize(
				true,
				false,
			)
			.then(
				(saved) => {
					if (saved) {
						next();
					} else {
						next(false);
					}
				},
				() => {
					next(false);

					// @ts-ignore: reload is our custom method and does exist
					window.App.router.reload();
				},
			);
	}
};

const isAddedToCart: NavigationGuard = (to, from, next) => {
	productAccess(
		to,
		from,
		(result) => {
			if (result === false) {
				next(false);
			} else {
				const cartItem = CartItemsModule.findWhere({
					productid: parseInt(
						to.params.productid,
						10,
					),
				});
				if (cartItem) {
					next();
				} else {
					next(false);
					navigate.toEnd(parseInt(
						to.params.productid,
						10,
					));
				}
			}
		},
	);
};

const loadShippingData: NavigationGuard = (to, from, next) => {
	if (!UserModule.id) {
		EventBus.$once(
			'auth:login',
			(success: boolean) => {
				if (success) {
					next();
				} else {
					next(false);
					navigate.toStart();
				}
			},
		);

		auth.showLogin({
			hasclose: false,
		});
	} else {
		ProductState
			.finalize(
				true,
				false,
			)
			.then(
				(saved) => {
					if (saved) {
						if (!ShippingModule.fetched) {
							const closeLoader = DialogService.openLoaderDialog();

							User
								.save()
								.then(() => ShippingModule.fetchCartShippingData({
									requestOptions: {
										params: {
											limit: 100,
										},
									},
								}))
								.then(() => {
									next();
								})
								.catch(() => {
									next(false);
								})
								.finally(() => {
									closeLoader();
								});
						} else {
							next();
						}
					} else {
						next(false);
					}
				},
				() => {
					next(false);
					window.App.router.reload();
				},
			);
	}
};

const showcaseAccess: NavigationGuard = (to, from, next) => {
	ProductsModule
		.addModel({
			id: parseInt(
				to.params.productid,
				10,
			),
			read_token: to.params.token,
		})
		.then(() => ProductState.setup(
			parseInt(
				to.params.productid,
				10,
			),
			{
				ajaxOptions: {
					headers: {
						'x-product-id': to.params.productid,
						'X-product-readtoken': to.params.token,
					},
				},
			},
		))
		.then(
			() => {
				next();
			},
			() => {
				const closeAlert = DialogService.openAlertDialog({
					header: {
						title: window.App.router.$t('dialogHeaderNoAccess'),
					},
					body: {
						content: window.App.router.$t('dialogTextNoAccess'),
					},
					footer: {
						buttons: [
							{
								id: 'accept',
								text: window.App.router.$t('dialogButtonNoAccessOk'),
								click: () => {
									// Product no longer exists, redirect to homepage
									window.location.href = '/';
									closeAlert();
								},
							},
						],
					},
				});

				next(false);
			},
		)
		.catch((err) => {
			DialogService.openErrorDialog({
				body: {
					content: err.message,
				},
			});
		});
};

const routes: RouteConfig[] = [
	{
		path: '/showcase/:productid/:token',
		component: ShowcaseModule,
		beforeEnter: showcaseAccess,
	},
	{
		path: '/create',
		component: CreateModule,
		beforeEnter: saveProduct,
		children: [{
			path: ':productcategoryid',
			component: CreateModule,
			beforeEnter: saveProduct,
			children: [{
				path: ':offeringid',
				component: CreateModule,
				beforeEnter: saveProduct,
				children: [{
					path: ':themecatid',
					component: CreateModule,
					beforeEnter: saveProduct,
					children: [{
						path: ':themeid',
						component: CreateModule,
						beforeEnter: saveProduct,
					}],
				}],
			}],
			meta: {
				keepScrollPosition: true,
			},
		}],
		meta: {
			keepScrollPosition: true,
			alsoKnownAs: ['/'],
		},
	},
	{
		path: '/pdp/:pdpid',
		component: CreateModule,
		beforeEnter: saveProduct,
	},
	{
		path: '/cart',
		component: CartModule,
		beforeEnter: loadShippingData,
	},
	{
		path: '/webshop/confirm',
		component: WebshopConfirmModule,
		beforeEnter: loadShippingData,
	},
	{
		path: '/webshop/payment',
		component: WebshopPaymentModule,
		beforeEnter: userAccess,
	},
	{
		path: '/webshop/:status',
		component: WebshopResultModule,
		beforeEnter: userAccess,
		children: [{
			path: ':orderid',
			component: WebshopResultModule,
			beforeEnter: userAccess,
		}],
	},
	{
		path: '/webshop',
		component: WebshopModule,
		beforeEnter: loadShippingData,
	},
	{
		path: '/oauth/:network',
		component: OauthModule,
	},
	{
		path: '/account/order/:orderid',
		component: OrderStatusModule,
		beforeEnter: userAccess,
	},
	{
		path: '/account/photos',
		component: UserAccountPhotosModule,
		beforeEnter: userAccess,
	},
	{
		path: '/account',
		component: UserAccountModule,
		beforeEnter: userAccess,
	},
	{
		path: '/launch/:offeringid',
		component: LauncherModule,
	},
	{
		path: '/:productid/open',
		beforeEnter(to, from, next) {
			const query = { ...to.query };

			// @ts-ignore: Custom method on App router
			window.App.router.openProduct(
				parseInt(
					to.params.productid,
					10,
				) as number | 'latest',
				true,
				query?.primaryAction as 'extendProjectLifetime' | 'removeProject',
			);
			next(false);
		},
	},
	{
		path: '/:productid/launch',
		component: LauncherModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/select/:source',
		component: ImportModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/library',
		component: LibraryModule,
		beforeEnter: productAccess,
		children: [{
			path: ':photoindex',
			component: LibraryModule,
			beforeEnter: productAccess,
		}],
	},
	{
		path: '/:productid/select',
		component: SelectModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/preview',
		component: PreviewModule,
		beforeEnter: productAccess,
		children: [{
			path: ':pagenr',
			component: PreviewModule,
			beforeEnter: productAccess,
		}],
	},
	{
		name: 'Pages Overview',
		path: '/:productid/overview*',
		component: OverviewModule,
		beforeEnter: productAccess,
	},
	{
		name: 'Sort',
		path: '/:productid/sort',
		component: SortModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/edit/:pagenr',
		component: EditorSwitchModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/crop/:objectid',
		component: CropItemModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/crop',
		component: CropOverviewModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/end',
		component: EndModule,
		beforeEnter: productAccess,
	},
	{
		path: '/:productid/confirm',
		component: ConfirmModule,
		beforeEnter: isAddedToCart,
	},
	{
		path: '/:productid/externalupload',
		component: ExternalUploadModule,
		beforeEnter: productSetup,
	},
	{
		path: '',
		component: ShelfModule,
		beforeEnter: saveProduct,
		meta: {
			keepScrollPosition: true,
		},
	},
];

export default routes;
